import { filter, tap, withLatestFrom, map, switchMap, timeout, catchError, take, toArray } from 'rxjs/operators';
import { fromEventPattern, forkJoin, of, iif, fromEvent } from 'rxjs';
import lodashGet from 'lodash/get';
import { emojiTypeNameMap } from '@/api/logical/im';
import { util } from '@/api/util/index';
import { api as masterApi } from '@/api/api';
import { apis } from '@/config';
const customMessageType = {
    1: '[石头剪刀布]',
    2: '[阅后即焚]',
    3: '[表情]',
    1010: '权限变更',
};
const getCustomContent = (msg) => {
    let content;
    try {
        content = JSON.parse(msg.content);
    }
    catch (ex) {
        content = {};
    }
    if (lodashGet(content, 'type', -99) === -99) {
        return '[当前版本暂不支持查看此消息类型]';
    }
    const { type: contentType, data, alt } = content;
    return [
        () => lodashGet(customMessageType, contentType, false),
        () => {
            const dataObj = typeof data === 'string' ? JSON.parse(data) : data;
            const headerStr = lodashGet(dataObj, 'header', false);
            return typeof headerStr === 'string' ? headerStr : false;
        },
        () => {
            const backupContent = typeof alt === 'string' ? JSON.parse(alt) : alt;
            return lodashGet(backupContent, 'header', false);
        },
        () => '[当前版本暂不支持查看此消息类型]',
    ].reduce((total, current) => {
        if (lodashGet(total, 'length', 0) === 0) {
            return current();
        }
        return total;
    }, '');
};
const convertMsgContent = (msg) => {
    if (lodashGet(msg, 'text.length', 0) !== 0) {
        return msg.text;
    }
    if (Object.keys(IM_PushNotify.MsgTypeDesc).includes(msg.type)) {
        return [`[${IM_PushNotify.MsgTypeDesc[msg.type]}]`, msg.type === 'file' ? msg.file.name : ''].join('');
    }
    if (msg.type === 'tip') {
        return msg.tip;
    }
    if (msg.type === 'custom') {
        return getCustomContent(msg);
    }
    return '[当前版本暂不支持查看此消息类型]';
};
export class IM_PushNotify {
    constructor() {
        this.sdk = null;
        this.pushApi = masterApi.requireLogicalApi(apis.pushApiImpl);
    }
    init(sdk) {
        this.sdk = sdk;
        this.onmsg(sdk);
        this.onquickcomment(sdk);
    }
    onmsg(sdk) {
        const onmsg = (handler) => {
            this.sdk.subscrible('onmsg', handler);
        };
        const offmsg = (handler) => {
            this.sdk.unSubcrible('onmsg', handler);
        };
        const $event = fromEventPattern(onmsg, offmsg);
        const $curSession = sdk.currentSession.getSubject();
        const $teamMuteStream = sdk.teamMuteStream.getSubject();
        const $visibleChange = fromEvent(document, 'visibilitychange').pipe(map(() => document.visibilityState));
        $event
            .pipe(filter(msg => msg.flow !== 'out'), filter(msg => !msg.isMuted), withLatestFrom($curSession, $visibleChange), filter(([msg, sessionId, visibilityState]) => {
            if (msg.type === 'notification') {
                return false;
            }
            if (visibilityState === 'hidden') {
                return true;
            }
            return msg.sessionId !== sessionId;
        }), map(([msg]) => msg), withLatestFrom($teamMuteStream), filter(([msg, teamMuteList]) => {
            if (msg.scene === 'team') {
                return !teamMuteList.includes(msg.to);
            }
            return true;
        }), map(([msg]) => msg), switchMap(msg => this.assemblePushContent(msg)))
            .subscribe(({ title, content, link }) => {
            this.pushApi.triggerNotificationInfoChange({
                action: 'new_im_msg',
                num: 1,
                content,
                title,
                data: link,
            });
        });
    }
    onquickcomment(sdk) {
        const onquickcomment = (handler) => {
            this.sdk.subscrible('onQuickComment', handler);
        };
        const offquickcomment = (handler) => {
            sdk.unSubcrible('onQuickComment', handler);
        };
        const $event = fromEventPattern(onquickcomment, offquickcomment);
        const $curSession = sdk.currentSession.getSubject();
        const $teamMuteStream = sdk.teamMuteStream.getSubject();
        const $p2pMuteStream = sdk.p2pMuteStream.getSubject();
        const $selfStream = sdk.imself.getSubject();
        const $visibleChange = fromEvent(document, 'visibilitychange').pipe(map(() => document.visibilityState));
        $event
            .pipe(tap(args => {
            console.log('[im.pushmsg]oncomment', args);
        }), withLatestFrom($selfStream), filter(([commentArgs, myinfo]) => {
            const [msg, comment] = commentArgs;
            if (msg.from !== myinfo.account) {
                return false;
            }
            return comment.from !== myinfo.account;
        }), map(([commentArgs]) => {
            const msg = commentArgs[0];
            commentArgs[0] = Object.assign(Object.assign({}, msg), { sessionId: [msg.scene, msg.to].join('-') });
            return commentArgs;
        }))
            .pipe(withLatestFrom($curSession, $visibleChange), filter(([commentArgs, sessionId, visibilityState]) => {
            if (visibilityState === 'hidden') {
                return true;
            }
            const [msg] = commentArgs;
            if (msg.scene === 'team') {
                return [msg.scene, msg.to].join('-') !== sessionId;
            }
            return ![`${msg.scene}-${msg.to}`, `${msg.scene}-${msg.from}`].includes(sessionId);
        }), map(([commentArgs]) => commentArgs))
            .pipe(withLatestFrom($teamMuteStream, $p2pMuteStream), filter(([commentArgs, teamMutelist, p2pMuteList]) => {
            const [msg] = commentArgs;
            const [scene, to] = msg.sessionId.split('-');
            if (scene === 'p2p') {
                return !p2pMuteList.includes(to);
            }
            return !teamMutelist.includes(to);
        }), map(args => {
            const [msg, comment] = args[0];
            const commentedId = msg.from;
            const text = emojiTypeNameMap[comment.body] || '[未知表情]';
            return Object.assign(Object.assign({}, msg), { type: 'text', text, flow: 'in', from: comment.from, commentTo: commentedId });
        }), tap(commentArgs => {
            console.log('[im.pushmsg]oncomment.handled', commentArgs);
        }), switchMap(msg => this.assemblePushContent(msg)))
            .subscribe(({ content, link, title }) => {
            this.pushApi.triggerNotificationInfoChange({
                action: 'new_im_msg',
                num: 1,
                content,
                title,
                data: link,
            });
        });
    }
    assemblePushContent(msg, isCommentMsg = false) {
        const $msgContent = of(msg).pipe(map(msg => {
            const msgContent = convertMsgContent(msg);
            return util.chopStrToSize(msgContent, 70);
        }));
        return iif(() => msg.scene === 'p2p', forkJoin({
            msgScene: of('p2p'),
            fromNick: this.getContactName(msg.from),
            teamName: of(''),
            msgContent: $msgContent,
        }).pipe(map(result => ({
            title: ['消息', result.fromNick].join(':'),
            content: [isCommentMsg ? '回应' : '', result.msgContent].filter(item => item.length >= 1).join(':'),
            link: `sessionId=p2p-${msg.from}&mode=normal&source=push`,
        }))), forkJoin({
            msgScene: of('team'),
            fromNick: this.getContactName(msg.from),
            teamName: this.getTeamName(msg.to),
            msgContent: $msgContent,
        }).pipe(map(result => ({
            title: ['群消息', result.teamName].join(':'),
            content: [result.fromNick + (isCommentMsg ? '回应' : ''), result.msgContent].join(':'),
            link: `sessionId=team-${msg.to}&mode=normal&source=push`,
        }))));
    }
    getTeamName(teamId) {
        this.sdk.imteamStream.requestTeamById(teamId);
        const $params = of(teamId, 'customTeamName').pipe(toArray());
        const $name = this.sdk.imteamStream.getTeamField($params);
        return $name.pipe(take(1), timeout(200), catchError(() => of('team')));
    }
    getContactName(userId) {
        this.sdk.imusers.requestUser(userId);
        const $name = this.sdk.imusers.getUserProp(of(userId), of('nick'));
        return $name.pipe(take(1));
    }
}
IM_PushNotify.MsgTypeDesc = {
    image: '图片',
    video: '视频',
    audio: '音频',
    file: '文件',
};
